import { useState, useEffect } from 'react';
import {
    Link2,
    CheckCircle2,
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    Search,
    RefreshCw,
    ExternalLink,
    ArrowRight,
    Loader2,
    XCircle,
    Filter
} from 'lucide-react';
import { authenticatedFetch } from '../utils/authenticatedFetch.js';
import { fetchAuthSession } from 'aws-amplify/auth';

export default function URLCheckPage({ stores }) {
    const [redirectResults, setRedirectResults] = useState({});
    const [loading, setLoading] = useState(false);
    const [checkingStore, setCheckingStore] = useState(null);
    const [expandedStores, setExpandedStores] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'redirected', 'clean'
    const [summary, setSummary] = useState({ total: 0, clean: 0, redirected: 0, errors: 0 });
    const [progress, setProgress] = useState({ completed: 0, total: 0, percent: 0 });
    const [elapsed, setElapsed] = useState(0);
    const [remaining, setRemaining] = useState(0);
    const [currentCheck, setCurrentCheck] = useState({ store: '', url: '' });

    // Helper function to get store results with ID-based lookup and fallbacks
    const getStoreResults = (store) => {
        // Try ID-based lookup first (most reliable)
        if (store._id && redirectResults[store._id]) {
            return redirectResults[store._id];
        }

        // Fallback to name-based lookup
        if (redirectResults[store.name]) {
            return redirectResults[store.name];
        }

        // Try case-insensitive name matching as last resort
        const normalizedStoreName = store.name.toLowerCase().trim();
        const matchingKey = Object.keys(redirectResults).find(
            key => key.toLowerCase().trim() === normalizedStoreName
        );

        if (matchingKey) {
            console.warn(`⚠️ Store lookup fallback: "${store.name}" matched "${matchingKey}"`);
            return redirectResults[matchingKey];
        }

        return null;
    };

    // Filter stores based on search and status
    const filteredStores = stores.filter(store => {
        // Search filter
        const matchesSearch = !searchQuery ||
            store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            store.region.toLowerCase().includes(searchQuery.toLowerCase());

        // Status filter
        if (filterStatus === 'all') return matchesSearch;

        const storeResults = getStoreResults(store);

        // If no results yet, don't show in filtered views (only in 'all')
        if (!storeResults) {
            console.debug(`🔍 Filter: No results found for store "${store.name}" (ID: ${store._id})`);
            return false;
        }

        if (filterStatus === 'redirected') {
            const hasIssues = storeResults.summary.redirected > 0 || storeResults.summary.errors > 0;
            return matchesSearch && hasIssues;
        }
        if (filterStatus === 'clean') {
            const isClean = storeResults.summary.redirected === 0 && storeResults.summary.errors === 0;
            return matchesSearch && isClean;
        }

        return matchesSearch;
    });

    // Play audio notification
    const playNotification = () => {
        try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKXh8LdlHQU2kdXx0HwvBSF1xe/glEILElyx6OyrWBUIQ5zd8sFuJAUuhM/z24o4CBpnvO3mnU8NDFCH4u+4ZxwGMo/T8M98MAUfcsLu4ZZGDBBY');
            audio.play().catch(() => { /* Ignore errors */ });
        } catch (error) {
            console.error('Audio playback failed:', error);
        }
    };

    // Check all stores with SSE progress
    const handleCheckAll = async () => {
        setLoading(true);
        setProgress({ completed: 0, total: 0, percent: 0 });
        setElapsed(0);
        setRemaining(0);
        setCurrentCheck({ store: '', url: '' });
        setRedirectResults({});
        setSummary({ total: 0, clean: 0, redirected: 0, errors: 0 });

        try {
            // Get authentication token for SSE
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                alert('No authentication token available. Please log in again.');
                setLoading(false);
                return;
            }

            // Pass token as query parameter since EventSource doesn't support headers
            const eventSource = new EventSource(`/api/stores/check-redirects-stream?token=${encodeURIComponent(token)}`);

            eventSource.addEventListener('start', (event) => {
                const data = JSON.parse(event.data);
                console.log('🚀 Starting redirect check:', data);
                setProgress(prev => ({
                    ...prev,
                    total: data.totalPages
                }));
            });

            eventSource.addEventListener('progress', (event) => {
                const data = JSON.parse(event.data);

                setProgress({
                    completed: data.completed,
                    total: data.total,
                    percent: data.progress
                });
                setElapsed(data.elapsed);
                setRemaining(data.remaining);
                setCurrentCheck({
                    store: data.currentStore,
                    url: data.currentUrl
                });
                setSummary(data.summary);
            });

            eventSource.addEventListener('complete', (event) => {
                const data = JSON.parse(event.data);
                console.log('✅ Check complete:', data);

                // Results already include both ID and name keys from backend
                setRedirectResults(data.results);
                setSummary(data.summary);

                // Expand all stores after checking (use store names for expansion tracking)
                const expanded = {};
                Object.values(data.results).forEach(storeResult => {
                    if (storeResult && storeResult.name) {
                        expanded[storeResult.name] = true;
                    }
                });
                setExpandedStores(expanded);

                // Play notification sound
                playNotification();

                eventSource.close();
                setLoading(false);
                setProgress(prev => ({ ...prev, percent: 100 }));

                console.log(`✅ Redirect check complete. ${data.summary.total} pages checked.`);
            });

            eventSource.addEventListener('error', (event) => {
                console.error('SSE error:', event);
                const data = event.data ? JSON.parse(event.data) : { message: 'Unknown error' };
                alert(`Error: ${data.message}`);
                eventSource.close();
                setLoading(false);
            });

            eventSource.onerror = (error) => {
                console.error('EventSource failed:', error);
                eventSource.close();
                setLoading(false);
                alert('Connection lost. Please try again.');
            };

        } catch (error) {
            console.error('Error checking redirects:', error);
            alert('Failed to start redirect check. Please try again.');
            setLoading(false);
        }
    };

    // Check single store
    const handleCheckStore = async (storeName) => {
        setCheckingStore(storeName);
        try {
            const response = await authenticatedFetch(`/api/stores/${encodeURIComponent(storeName)}/check-redirects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (data.success) {
                // Store results by both ID and name for reliable lookup
                const storeResult = {
                    name: data.name,
                    id: data.id,
                    pages: data.pages,
                    summary: data.summary
                };

                setRedirectResults(prev => ({
                    ...prev,
                    [data.id]: storeResult,      // Primary key: ID
                    [data.name]: storeResult,    // Fallback key: name
                    [storeName]: storeResult     // Also store by the name used in request
                }));

                // Update summary
                setSummary(prev => ({
                    total: prev.total + data.summary.total,
                    clean: prev.clean + data.summary.clean,
                    redirected: prev.redirected + data.summary.redirected,
                    errors: prev.errors + data.summary.errors
                }));

                // Expand the store
                setExpandedStores(prev => ({ ...prev, [storeName]: true }));

                console.log(`✅ Store check complete: ${storeName} (ID: ${data.id})`);
            } else {
                console.error('Store check failed:', data.error);
                alert(`Error: ${data.error}`);
            }
        } catch (error) {
            console.error('Error checking store:', error);
            alert('Failed to check store. Please try again.');
        } finally {
            setCheckingStore(null);
        }
    };

    // Toggle store expansion
    const toggleStore = (storeName) => {
        setExpandedStores(prev => ({
            ...prev,
            [storeName]: !prev[storeName]
        }));
    };

    // Get status badge for store
    const getStoreBadge = (store) => {
        const storeResults = getStoreResults(store);

        if (!storeResults) {
            return <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">Not Checked</span>;
        }

        if (storeResults.summary.errors > 0) {
            return (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 flex items-center gap-1.5">
                    <XCircle size={14} />
                    {storeResults.summary.errors} Error{storeResults.summary.errors !== 1 ? 's' : ''}
                </span>
            );
        }

        if (storeResults.summary.redirected > 0) {
            return (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700 flex items-center gap-1.5">
                    <AlertTriangle size={14} />
                    {storeResults.summary.redirected} Redirect{storeResults.summary.redirected !== 1 ? 's' : ''}
                </span>
            );
        }

        return (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 size={14} />
                Clean
            </span>
        );
    };

    // Get status icon for page
    const getPageStatusIcon = (page) => {
        if (page.status === 'error') {
            return <XCircle size={18} className="text-red-500" />;
        }
        if (page.redirected) {
            return <AlertTriangle size={18} className="text-amber-500" />;
        }
        return <CheckCircle2 size={18} className="text-emerald-500" />;
    };

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-8 border border-slate-200/60">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-200/60">
                            <Link2 size={32} className="text-orange-600" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-slate-900">URL Check</h2>
                            <p className="text-slate-600 mt-1 font-medium">Monitor redirect status for all landing pages</p>
                        </div>
                    </div>
                    <button
                        onClick={handleCheckAll}
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-400 text-white rounded-xl font-medium shadow-lg transition-all duration-200 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                Checking {progress.completed}/{progress.total}...
                            </>
                        ) : (
                            <>
                                <RefreshCw size={20} />
                                Check All Stores
                            </>
                        )}
                    </button>
                </div>

                {/* Progress Bar */}
                {loading && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                            <div className="flex items-center gap-3">
                                <Loader2 size={16} className="animate-spin text-orange-600" />
                                <span>Checking {currentCheck.store}...</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-slate-600">
                                    ⏱️ {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
                                </span>
                                {remaining > 0 && (
                                    <span className="text-slate-600">
                                        ⏳ ~{Math.floor(remaining / 60)}:{(remaining % 60).toString().padStart(2, '0')} remaining
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div
                                className="absolute h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-300 ease-out"
                                style={{ width: `${progress.percent}%` }}
                            />
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                            Current: {currentCheck.url}
                        </div>
                    </div>
                )}

                {/* Summary Stats */}
                {summary.total > 0 && (
                    <div className={`grid grid-cols-4 gap-4 ${loading ? 'mt-4' : ''}`}>
                        <div className="bg-white/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="text-2xl font-bold text-slate-900">{summary.total}</div>
                            <div className="text-sm font-medium text-slate-600">Total Pages</div>
                        </div>
                        <div className="bg-emerald-50/80 rounded-xl p-4 border border-emerald-200/60">
                            <div className="text-2xl font-bold text-emerald-700">{summary.clean}</div>
                            <div className="text-sm font-medium text-emerald-600">Clean</div>
                        </div>
                        <div className="bg-amber-50/80 rounded-xl p-4 border border-amber-200/60">
                            <div className="text-2xl font-bold text-amber-700">{summary.redirected}</div>
                            <div className="text-sm font-medium text-amber-600">Redirected</div>
                        </div>
                        <div className="bg-red-50/80 rounded-xl p-4 border border-red-200/60">
                            <div className="text-2xl font-bold text-red-700">{summary.errors}</div>
                            <div className="text-sm font-medium text-red-600">Errors</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Filters and Search */}
            <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search stores..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    />
                </div>
                <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 p-1">
                    <button
                        onClick={() => setFilterStatus('all')}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${filterStatus === 'all'
                            ? 'bg-orange-100 text-orange-700'
                            : 'text-slate-600 hover:bg-slate-50'
                            }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilterStatus('redirected')}
                        className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 ${filterStatus === 'redirected'
                            ? 'bg-amber-100 text-amber-700'
                            : 'text-slate-600 hover:bg-slate-50'
                            }`}
                    >
                        <AlertTriangle size={16} />
                        Redirected
                    </button>
                    <button
                        onClick={() => setFilterStatus('clean')}
                        className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 ${filterStatus === 'clean'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'text-slate-600 hover:bg-slate-50'
                            }`}
                    >
                        <CheckCircle2 size={16} />
                        Clean
                    </button>
                </div>
            </div>

            {/* Store List */}
            <div className="space-y-4">
                {filteredStores.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                        <Filter size={48} className="mx-auto text-slate-400 mb-4" />
                        <p className="text-lg font-medium text-slate-600">No stores match your filters</p>
                        <p className="text-slate-500 mt-2">Try adjusting your search or filter settings</p>
                    </div>
                ) : (
                    filteredStores.map(store => {
                        const storeResults = getStoreResults(store);
                        const isExpanded = expandedStores[store.name];
                        const isChecking = checkingStore === store.name;

                        return (
                            <div
                                key={store._id}
                                className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md"
                            >
                                {/* Store Header */}
                                <div className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-50 to-white">
                                    <div className="flex items-center gap-4 flex-1">
                                        <button
                                            onClick={() => toggleStore(store.name)}
                                            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                                        >
                                            {isExpanded ? (
                                                <ChevronDown size={20} className="text-slate-600" />
                                            ) : (
                                                <ChevronRight size={20} className="text-slate-600" />
                                            )}
                                        </button>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-slate-900">{store.name}</h3>
                                            <p className="text-sm text-slate-600">{store.pages.length} landing page{store.pages.length !== 1 ? 's' : ''}</p>
                                        </div>
                                        {getStoreBadge(store)}
                                    </div>
                                    <button
                                        onClick={() => handleCheckStore(store.name)}
                                        disabled={isChecking || loading}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-medium transition-all duration-200 disabled:cursor-not-allowed ml-4"
                                    >
                                        {isChecking ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Checking...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw size={16} />
                                                Check Store
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* Store Pages */}
                                {isExpanded && storeResults && (
                                    <div className="p-6 pt-0 space-y-3">
                                        {storeResults.pages.map((page, index) => (
                                            <div
                                                key={index}
                                                className={`p-4 rounded-xl border-2 ${page.status === 'error'
                                                    ? 'bg-red-50 border-red-200'
                                                    : page.redirected
                                                        ? 'bg-amber-50 border-amber-200'
                                                        : 'bg-emerald-50 border-emerald-200'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start gap-3 flex-1">
                                                        {getPageStatusIcon(page)}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="font-bold text-slate-900">{page.type}</span>
                                                                <span className="text-sm px-2 py-0.5 bg-white rounded-full text-slate-600 border">
                                                                    {page.domain}
                                                                </span>
                                                            </div>

                                                            {page.status === 'error' ? (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2 text-sm mb-2">
                                                                        <span className="font-medium text-slate-700">URL:</span>
                                                                        <a
                                                                            href={page.originalUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-blue-600 hover:underline font-mono text-xs truncate flex items-center gap-1"
                                                                        >
                                                                            {page.originalUrl}
                                                                            <ExternalLink size={12} />
                                                                        </a>
                                                                    </div>
                                                                    <div className="text-sm text-red-700 font-medium">
                                                                        {page.message}
                                                                    </div>
                                                                    {page.wordpress404 && page.wordpress404.is404 && (
                                                                        <div className="mt-2 p-3 bg-red-100 border border-red-300 rounded-lg">
                                                                            <div className="text-xs font-semibold text-red-800 mb-1">
                                                                                WordPress 404 Detection Details:
                                                                            </div>
                                                                            <div className="text-xs text-red-700 space-y-1">
                                                                                <div>Confidence: {page.wordpress404.confidence}%</div>
                                                                                <div className="font-medium">Matched Patterns:</div>
                                                                                <ul className="list-disc list-inside pl-2">
                                                                                    {page.wordpress404.matchedPatterns.map((pattern, idx) => (
                                                                                        <li key={idx}>{pattern}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2 text-sm">
                                                                        <span className="font-medium text-slate-700">Original:</span>
                                                                        <a
                                                                            href={page.originalUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-blue-600 hover:underline font-mono text-xs truncate flex items-center gap-1"
                                                                        >
                                                                            {page.originalUrl}
                                                                            <ExternalLink size={12} />
                                                                        </a>
                                                                    </div>

                                                                    {page.redirected && (
                                                                        <>
                                                                            <div className="flex items-center gap-2">
                                                                                <ArrowRight size={16} className="text-amber-600" />
                                                                            </div>
                                                                            <div className="flex items-center gap-2 text-sm">
                                                                                <span className="font-medium text-slate-700">Final:</span>
                                                                                <a
                                                                                    href={page.finalUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-amber-700 hover:underline font-mono text-xs truncate flex items-center gap-1 font-bold"
                                                                                >
                                                                                    {page.finalUrl}
                                                                                    <ExternalLink size={12} />
                                                                                </a>
                                                                            </div>
                                                                        </>
                                                                    )}

                                                                    {!page.redirected && (
                                                                        <div className="text-sm text-emerald-700 font-medium flex items-center gap-2">
                                                                            <CheckCircle2 size={14} />
                                                                            No redirect detected
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${page.score >= 90 ? 'bg-emerald-100 text-emerald-700' :
                                                        page.score >= 75 ? 'bg-blue-100 text-blue-700' :
                                                            page.score >= 50 ? 'bg-amber-100 text-amber-700' :
                                                                'bg-red-100 text-red-700'
                                                        }`}>
                                                        {page.score}%
                                                    </div>
                                                </div>

                                                {/* Show redirect chain if available */}
                                                {page.redirectChain && page.redirectChain.length > 2 && (
                                                    <details className="mt-3 pt-3 border-t border-slate-200">
                                                        <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-800">
                                                            View Redirect Chain ({page.redirectChain.length} steps)
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {page.redirectChain.map((url, idx) => (
                                                                <div key={idx} className="flex items-start gap-2 text-xs">
                                                                    <span className="bg-slate-200 text-slate-700 rounded-full w-5 h-5 flex items-center justify-center font-bold flex-shrink-0">
                                                                        {idx + 1}
                                                                    </span>
                                                                    <span className="font-mono text-slate-600 break-all">{url}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Empty State */}
            {stores.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                    <Link2 size={48} className="mx-auto text-slate-400 mb-4" />
                    <p className="text-lg font-medium text-slate-600">No stores available</p>
                    <p className="text-slate-500 mt-2">Add stores to check their URL redirect status</p>
                </div>
            )}
        </div>
    );
}
